import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  Users,
  Inbox,
  UserCog,
  LogOut,
  Moon,
  Sun,
  Copy,
  Menu,
  LayoutGrid,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import NotificationBell from "../NotificationBell";
import logo from "../../assets/access wealth logo icon light.png";
import logo2 from "../../assets/access wealth logo icon.png";

// The advisor workspace is deliberately themed differently from the personal
// app: an emerald/amber identity on a dark rail, vs the user app's emerald.
const NAV = [
  { to: "/advisor", label: "Clients", icon: Users, end: true },
  { to: "/advisor/workspace", label: "Client Workspace", icon: LayoutGrid },
  { to: "/advisor/messages", label: "Messages & Tasks", icon: MessageSquare },
  {
    to: "/advisor/approvals",
    label: "Approvals",
    icon: Inbox,
    badge: "pending",
  },
  { to: "/advisor/profile", label: "My Profile", icon: UserCog },
];

export default function AdvisorLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/pfa/me");
      setProfile(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadProfile();
    const onQueued = () => loadProfile();
    window.addEventListener("pfa:request-queued", onQueued);
    return () => window.removeEventListener("pfa:request-queued", onQueued);
  }, [loadProfile]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const copyCode = () => {
    if (!profile?.advisor_code) return;
    navigator.clipboard?.writeText(profile.advisor_code);
    toast.success("Advisor code copied");
  };

  const pending = profile?.pending_requests || 0;

  const Rail = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      {/* BRAND */}
      <Link
        to="/advisor"
        onClick={onNavigate}
        className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200 dark:border-slate-800"
      >
        <img
          src={theme === "dark" ? logo : logo2}
          alt="Access Wealth"
          className="h-9 w-9 object-cover"
        />
        <div className="leading-tight">
          <div className="font-display font-bold text-slate-900 dark:text-white">
            AccessWealth
          </div>
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-600 dark:text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
            Advisor Console
          </div>
        </div>
      </Link>

      {/* ADVISOR CODE CHIP */}
      <div className="px-4 pt-4">
        <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700/80 dark:text-emerald-300/80 mb-1">
            Your advisor code
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-white truncate">
              {profile?.advisor_code || "—"}
            </span>
            <button
              onClick={copyCode}
              className="grid h-7 w-7 place-items-center rounded-lg text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
              title="Copy code"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* NAV */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                isActive
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.badge === "pending" && pending > 0 && (
              <span className="grid h-5 min-w-5 px-1 place-items-center rounded-full bg-amber-400 text-[11px] font-bold text-slate-900">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* FOOTER: account */}
      <div className="px-3 py-4 border-t border-slate-200 dark:border-slate-800 space-y-1">
        <button
          onClick={toggle}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
          Appearance
        </button>
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-500/20 dark:to-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm font-bold ring-1 ring-emerald-200 dark:ring-emerald-500/30">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
              {user?.name || "Advisor"}
            </div>
            <div className="truncate text-[11px] text-slate-400">
              {user?.email}
            </div>
          </div>
          <button
            onClick={onLogout}
            title="Logout"
            className="grid h-8 w-8 place-items-center rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* DESKTOP RAIL */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
        <Rail />
      </aside>

      {/* MOBILE RAIL */}
      {mobileNav && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileNav(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
            <Rail onNavigate={() => setMobileNav(false)} />
          </aside>
        </>
      )}

      {/* CONTENT */}
      <div className="flex-1 lg:ml-64">
        {/* MOBILE TOPBAR */}
        <header className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <button
            onClick={() => setMobileNav(true)}
            className="grid h-9 w-9 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-display font-bold">Advisor Console</span>
          <div className="flex items-center gap-1">
            <NotificationBell enabled={!!user} />
            <button
              onClick={onLogout}
              className="grid h-9 w-9 place-items-center rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Desktop top strip with the notification center */}
        <header className="hidden lg:flex sticky top-0 z-30 h-14 items-center justify-end gap-2 px-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <NotificationBell enabled={!!user} />
        </header>

        {/* Slim gold identity accent */}
        <div className="h-0.5 bg-gradient-to-r from-amber-400 to-amber-300" />

        <main className="p-4 sm:p-6 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
